import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { useTheme } from '../../hooks/useTheme';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { Mail, Lock, Eye, EyeOff, Building2, Truck, Wrench } from 'lucide-react';

// Datos dummy para simular autenticación
const DUMMY_USERS = [
  { email: 'cliente@demo.cl',       password: 'demo1234', role: 'broker',           name: 'Juan Rodríguez' },
  { email: 'transportista@demo.cl', password: 'demo1234', role: 'carrier',          name: 'Carlos Vera' },
  { email: 'proveedor@demo.cl',     password: 'demo1234', role: 'service_provider', name: 'Patio Pajaritos' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [errors, setErrors]     = useState<{ email?: string; password?: string }>({});

  function validate() {
    const e: typeof errors = {};
    if (!email)    e.email    = 'Ingresa tu correo electrónico';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Correo electrónico inválido';
    if (!password) e.password = 'Ingresa tu contraseña';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setLoading(true);
    // Simular delay de red
    await new Promise(r => setTimeout(r, 900));

    const user = DUMMY_USERS.find(
      u => u.email === email && u.password === password
    );

    if (!user) {
      setError('Correo o contraseña incorrectos. Intenta con los datos de demo.');
      setLoading(false);
      return;
    }

    // Guardar sesión dummy en localStorage
    localStorage.setItem('auth_user', JSON.stringify(user));
    navigate('/dashboard');
  }

  function fillDemo(role: typeof DUMMY_USERS[0]) {
    setEmail(role.email);
    setPassword(role.password);
    setError('');
    setErrors({});
  }

  return (
    <div className="min-h-screen bg-surface dark:bg-dark-surface flex flex-col">

      {/* Header mínimo */}
      <header className="flex items-center justify-between px-4 py-8 max-w-sm mx-auto w-full">
        <img
          src={isDark ? '/logo_cargalo-dark.png' : '/logo_cargalo.png'}
          alt="Cárgalo"
          className="h-9 w-auto"
        />
        <ThemeToggle />
      </header>

      {/* Card de login */}
      <main className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-sm">

          {/* Título */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-1">
              Bienvenido de vuelta
            </h1>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              Ingresa a tu cuenta para continuar
            </p>
          </div>

          {/* Error global */}
          {error && (
            <Alert type="error" className="mb-5">
              {error}
            </Alert>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            <Input
              label="Correo electrónico"
              type="email"
              placeholder="tu@correo.cl"
              autoComplete="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
              error={errors.email}
              leftIcon={<Mail className="w-4 h-4 text-gray-400" strokeWidth={2} />}
            />

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-semibold text-gray-700 dark:text-dark-text">
                  Contraseña
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-broker hover:text-broker-light transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Input
                id="password"
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                value={password}
                onChange={e => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                error={errors.password}
                leftIcon={<Lock className="w-4 h-4 text-gray-400" strokeWidth={2} />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPass(v => !v)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                  </button>
                }
              />
            </div>

            <Button type="submit" fullWidth size="lg" loading={loading} className="mt-2">
              {loading ? 'Ingresando…' : 'Ingresar'}
            </Button>

          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200 dark:bg-dark-border" />
            <span className="text-xs text-gray-400 dark:text-dark-text-muted font-medium">O ingresa con cuenta demo</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-dark-border" />
          </div>

          {/* Accesos rápidos demo */}
          <div className="space-y-2">
            {DUMMY_USERS.map(u => {
              const config = {
                broker:           { label: 'Cliente',               icon: Building2, color: 'text-broker dark:text-broker-dark-text' },
                carrier:          { label: 'Transportista',         icon: Truck,     color: 'text-carrier dark:text-carrier-dark-text' },
                service_provider: { label: 'Proveedor de servicios', icon: Wrench,    color: 'text-provider dark:text-provider-dark-text' },
              }[u.role]!;
              const IconComponent = config.icon;
              return (
                <button
                  key={u.role}
                  type="button"
                  onClick={() => fillDemo(u)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card hover:border-gray-300 dark:hover:border-gray-500 text-sm font-medium transition-all duration-150"
                >
                  <IconComponent className={`w-5 h-5 ${config.color}`} strokeWidth={2} />
                  <div className="text-left">
                    <p className={`font-semibold ${config.color}`}>{config.label}</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted">{u.email}</p>
                  </div>
                  <span className="ml-auto text-xs text-gray-400 dark:text-dark-text-muted">Usar →</span>
                </button>
              );
            })}
          </div>

          {/* Link a registro */}
          <p className="text-center text-sm text-gray-500 dark:text-dark-text-muted mt-8">
            ¿Aún no tienes cuenta?{' '}
            <Link
              to="/register"
              className="font-semibold text-broker hover:text-broker-light transition-colors"
            >
              Crear cuenta gratis
            </Link>
          </p>

        </div>
      </main>
    </div>
  );
}
